import axios from 'axios';
import User from './User.js';

import Config from '../config/config.js';

let instance = axios.create({
  baseURL: Config.train.url,
  headers: {
    post: {'Content-Type': 'application/json'}
  }
});

instance.interceptors.request.use( (config) => {
  
  if (User.getToken()) {
    config.headers['Authorization'] = `Bearer ${User.getToken()}`;

  }

  if (User.getGoogleToken()) {
    config.headers['Gsuite-Token'] = `${User.getGoogleToken()}`;
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

let itemsPerFetch = 50;

let request = ({method, path, params = {}}) => {

  if (method === 'get')
    params = {params};

	return instance[method](`/${path}`, params)
	.then((response) => {

    let data = response.data;

    if (typeof(data) === 'object') {

      let headers = response.headers;

      let pagination = {
        page: headers['x-page'], 
        perPage: headers['x-per-page'], 
        total: headers['x-total']
      };

      if (pagination.page !== undefined && pagination.perPage !== undefined && pagination.total !== undefined)
        data.pagination = pagination;

    }
        
    return data;
	})
  .catch((error, response) => {
    if (error.response && error.response.status === 403) {
      User.clearSession();
    }
    return Promise.reject(error);
  });
};

let getRequest = ({resource, path, params = {}}) => {
	return request({method: 'get', path, params})
};

let putRequest = ({resource, path, params = {}}) => {
  return request({method: 'put', path, params})
};

let postRequest = ({resource, path, params = {}}) => {
  return request({method: 'post', path, params})
};

let buildParams = (period, discovered, sort, month, year, newOnly, userId, genders, tags, countries, tourCountries, forgotten, timePeriods) => {

  let params = {};

  if (period)
    params.period = period;

  if (discovered)
    params.discovered = discovered;

  if (forgotten)
    params.daysNotHeard = 90;

  if (sort)
    params.sort = sort;

  if (month !== null && year !== null) {
    params.month = month;
    params.year = year;
  }

  if (newOnly)
    params.new = newOnly;

  if (tourCountries)
    params.tourCountry = tourCountries;

  if (countries)
    params.country = countries;

  if (tags) 
    params.tag = tags;

  if (genders)
    params.gender = genders;

  if (timePeriods)
    params.timePeriod = timePeriods;

  return params;
}

let buildFilters = (period, discovered, sort, month, year, newOnly, userId, genders, tags, countries, tourCountries, forgotten, timePeriods) => {
  let filterTerms = {};

  if (genders)
    filterTerms.genders = genders;

  if (tags)
    filterTerms.tags = ['any'].concat(tags || []);

  if (countries)
    filterTerms.countries = countries;

  return filterTerms;
}

let Train = {

  syncTop: ({service}) => {

    let user = User.getId();

    return putRequest({
      path: `users/${user}/sync_top`,
      params: {
        service
      }
    }); 
  },

  discoverArtist: ({artist, status}) => {

    let user = User.getId();

    return putRequest({
      path: `users/${user}/artists/discover`,
      params: {
        artist, status
    }});
    
  },

  loveTrack: ({track, player, status}) => {

    let user = User.getId();

    return putRequest({
      path: `users/${user}/tracks/love`,
      params: {
        album: track.album, artist: track.artist, track: track.track, player, status
    }});
    
  },

  scrobble: ({track, player}) => {

    let user = User.getId();
    return postRequest({
      path: `users/${user}/tracks/scrobble`,
      params: {
        album: track.album, artist: track.artist, track: track.track, player
    }});
    
  },

  uploadUserPicture: ({file}) => {

    let user = User.getId();
    return postRequest({
      path: `users/${user}/profile_picture`,
      params: file
    });
    
  },

  userServiceData: (service) => {

    let user = User.getId();
    return getRequest({
      path: `users/${user}/${service}_credentials`,
    });
    
  },

  authorizeLastfm: ({token}) => {

    let user = User.getId();
    return postRequest({
      path: `users/${user}/lastfm_credentials`,
      params: {token}
    });
  },

  authorizeSpotify: ({code}) => {

    let user = User.getId();
    return postRequest({
      path: `users/${user}/spotify_credentials`,
      params: {code}
    });
  },

  authorizeLibrefm: ({token}) => {

    let user = User.getId();
    return postRequest({
      path: `users/${user}/librefm_credentials`,
      params: {token}
    });
    
  },

  authorizeFacebook: ({accessToken}) => {

    let user = User.getId();
    return postRequest({
      path: `users/${user}/facebook_credentials`,
      params: {accessToken}
    });
    
  },

  requestTwitterAuthorization: () => {

    let user = User.getId();
    return getRequest({
      path: `users/${user}/twitter_token`
    });
    
  },

  authorizeTwitter: ({oauth_token, oauth_verifier}) => {

    let user = User.getId();
    return postRequest({
      path: `users/${user}/twitter_credentials`,
      params: {oauth_token, oauth_verifier}
    });
    
  },

  authenticateUser: ({email, password}) => {
    return postRequest({
      path: `users/authenticate`,
      params: {
        email, password
      }
    });
  },

  changePassword: ({password, newPassword}) => {
    let user = User.getId();
    return putRequest({
      path: `users/${user}/password`,
      params: {
        password, newPassword
      }
    });
  },

  setNewPassword: ({newPassword, token}) => {
    return putRequest({
      path: `users/new_password`,
      params: {
        newPassword, token
      }
    });
  },

  createUser: ({username, email, password}) => {
    return postRequest({
      path: `users`,
      params: {
        username, email, password
      }
    });
  },

  sendRecoveryEmail: ({email}) => {
    return postRequest({
      path: `users/password_retrieval`,
      params: {
        email
      }
    });
  },

  twitterLogin: () => {
    return getRequest({
      path: `signup/twitter`
    });
  },

  twitterSignup: ({oauth_token, oauth_verifier}) => {
    return postRequest({
      path: `signup/twitter`, params: {
        oauth_token, oauth_verifier
      }
    });
  },

  facebookSignup: ({accessToken}) => {
    return postRequest({
      path: `signup/facebook`, params: {
        accessToken
      }
    });
  },

	fetchPlayerTracks: ({countryCode, player = 'yt', type = 'artist', terms = []}) => {
		return getRequest({
			path: `player/search/tracks/${player}/${type}`,
			params: {
        country: countryCode,
				terms: JSON.stringify(terms)
			}
		});
	},

  fetchArtist: ({artist}) => {
    return getRequest({
      path: `artists/${artist}`,
      params: {}
    });
  },

  fetchArtistProfile: ({element: artist}) => {
    return getRequest({
      path: `artists/${encodeURIComponent(artist)}/profile`,
      params: {}
    });
  },

  fetchArtistsProfiles: ({artists}) => {
    return getRequest({
      path: `artists/profiles`,
      params: {artists}
    });
  },

  fetchLastfmUser: ({user}) => {
    return getRequest({
      path: `lastfm_users/${user}`,
      params: {}
    });
  },

  fetchTag: ({tag}) => {
    return getRequest({
      path: `tags/${encodeURIComponent(tag)}`,
      params: {}
    });
  },

  fetchTrack: ({artist, track}) => {
    return getRequest({
      path: `tracks/${encodeURIComponent(track)}/${encodeURIComponent(artist)}`,
      params: {}
    });
  },

  fetchAlbum: ({artist, album}) => {
    return getRequest({
      path: `albums/${encodeURIComponent(album)}/${encodeURIComponent(artist)}`,
      params: {}
    });
  },

  fetchUser: ({user}) => {
    return getRequest({
      path: `users/${user}`,
      params: {}
    });
  },

  fetchPopularElements: ({popular, type}) => {
    return getRequest({
      path: `populars/${popular}/${type}`
    });
  },

  fetchPopularLists: () => {
    return getRequest({
      path: 'populars'
    });
  },

  fetchUsersDiscoveries: ({page = 1, sort = 'top', max = itemsPerFetch}) => {
    return getRequest({
      path: 'users/artists',
      params: {discovered: true, sort, page, max}
    });
  },

  fetchUserArtist: ({user, artist}) => {
    return getRequest({
      path: `users/${user}/artists/${encodeURIComponent(artist)}`
    });
  },

  fetchUserTrack: ({user, artist, track}) => {
    return getRequest({
      path: `users/${user}/tracks/${encodeURIComponent(track)}/artists/${encodeURIComponent(artist)}`
    });
  },

  fetchUserLatestArtists: ({user}) => {
    return getRequest({
      path: `users/${user}/latest_artists`
    });
  },

  fetchUserLatestTracks: ({user}) => {
    return getRequest({
      path: `users/${user}/latest_tracks`
    });
  },

  fetchUserArtists: ({user, match = '', gender, daysNotHeard = 0, discovered = false, max = itemsPerFetch, page = 1, sort = 'top', month, year, newOnly, country, tag, timePeriod, tourCountry}) => {
    return getRequest({
      path: `users/${user}/artists/`, 
      params: {match, daysNotHeard, discovered, max, page, sort, gender, month, year, new: newOnly, country, tag, timePeriod, tourCountry}
    });
  },

  fetchUserTracks: ({user, match = '', daysNotHeard = 0, loved = false, max = itemsPerFetch, page = 1, sort = 'top'}) => {
    return getRequest({
      path: `users/${user}/tracks/`, 
      params: {match, daysNotHeard, loved, max, page, sort}
    });
  },

  fetchUserScrobbles: ({user, max = itemsPerFetch, page = 1, match = '', year, month}) => {
    return getRequest({
      path: `users/${user}/scrobbles/`, 
      params: {max, page, match, year, month}
    });
  },

  fetchUserTopArtists: ({user, period}) => {
    return getRequest({
      path: `users/${user}/artists/top/${period}/artists`
    });
  },

  fetchUserTopTracks: ({user, period}) => {
    return getRequest({
      path: `users/${user}/artists/top/${period}/tracks`
    });
  },

  fetchUserArtistSuggestions: ({user, period}) => {
    return getRequest({
      path: `users/${user}/artists/suggestions/${period}`
    });
  },

  fetchUserSummary: ({user, type, query}) => {
    if (query) {
      query['new'] = query['new'] || query.newOnly;
    }
    return getRequest({
      path: `users/${user}/summary/${type}`,
      params: query
    });
  },

  fetchUserFacets: ({user, type, query}) => {
    if (query) {
      query['new'] = query['new'] || query.newOnly;
    }
    return getRequest({
      path: `users/${user}/facets`,
      params: query
    });
  },

	fetchSearchResults: ({query}) => {
		return getRequest({
			path: `search/${query.trim().toLowerCase()}`
		});
	},

  fetchMapData: ({context, searchTerm, mapType, mapTypeTerm, elementsNumber = 85, period, discovered, sort, month, year, newOnly, userId, genders, tags, countries, tourCountries, forgotten, timePeriods}) => {
    let path = 'maps/' + context + '/' + encodeURIComponent(searchTerm) + '/' + mapType + '/' + mapTypeTerm;

    let params = buildParams(period, discovered, sort, month, year, newOnly, userId, genders, tags, countries, tourCountries, forgotten, timePeriods);
    let filterTerms = buildFilters(period, discovered, sort, month, year, newOnly, userId, genders, tags, countries, tourCountries, forgotten, timePeriods);

    return getRequest({
      path,
      params: {
        params: JSON.stringify(params), 
        filterTerms: JSON.stringify(filterTerms), 
        maxElementsNumber: elementsNumber,
        userId
      },
    });
  },

  fetchRiverData: ({context, searchTerm, riverType, riverTypeTerm, from, to}) => {
    let path = 'rivers/' + context + '/' + encodeURIComponent(searchTerm) + '/' + riverType + '/' + riverTypeTerm;

    return getRequest({
      path,
      params: {
        drawingType: 0,
        from,
        to
      }
    });
  },

  fetchTreemapData: ({context, searchTerm, treemapType, treemapTypeTerm, period, discovered, loved, sort}) => {
    let path = 'treemaps/' + context + '/' + encodeURIComponent(searchTerm) + '/' + treemapType + '/' + treemapTypeTerm;

    return getRequest({
      path,
      params: {
        period, discovered, sort, loved
      }
    });
  },

  fetchPicture: ({type, element}) => {
    let path = 'pictures';
    return getRequest({
      path,
      params: {
        searchTerm: element, selectionType: type
      }
    });
  },

  fetchTags: ({type, element}) => {
    let path = `tags/${type}/${element}`;
    return getRequest({
      path
    });
  },

  fetchTopTags: () => {
    let path = `tags/`;
    return getRequest({
      path
    });
  },

  fetchGoogleUrl: () => {
    let path = `google/url`;
    return getRequest({
      path
    });
  },

  authorizeGoogle: (code) => {
    let path = `google/authorize`;
    return postRequest({
      path,
      params: {code}
    });
  }


};

module.exports = Train



// WEBPACK FOOTER //
// ./src/js/services/train.js